import { E as EVENTS } from './events-58bc9098.js';

class ProductBlockVariantSelector extends HTMLElement {
  constructor() {
    super();
    this._debugEcopack = true;

    this.selectors = {
      ecopackToggle:
        "[js-ecopack-toggle], product-block-ecopack input[type='checkbox'], .ecopack input[type='checkbox']",
      ecoPrice: ".eco-price",
      ecoOriginalPrice: "[js-ecopack-original-price], .original-price",
      ecoDiscountedPrice:
        "[js-ecopack-discounted-price], .discounted-price",
      variantButton: "[js-variant-button]",
      variantGroup: "[js-variant-group-container]",
      variantLabelValue: "[js-variant-label-value]",
      variantOption: "[js-variant-option]",
      variantInput: "[js-variant-input]",
      noJsVariantSelector: "[js-no-js-variant-selector",
    };
  }

  connectedCallback() {
    this.controller = new AbortController();
    this.productSection = this.closest("[js-product-section]");
    this.logEcopack("connected");

    // Listen for variant change
    this.initActiveVariants();
    this.initEcoPriceModels();
    this.syncEcoPriceToSelectedVariant();
    this.initEcopack();

    // Reinitialize listeners on variant change
    this.addEventListener(
      EVENTS.PRODUCT_VARIANT_CHANGED,
      this.handleRepaint.bind(this),
      { signal: this.controller.signal },
    );
  }

  // TODO: This gets called a lot, see if there's a more performant solution
  initActiveVariants() {
    this.activeVariantsController = new AbortController();

    // Remove no-js variant selector to prevent conflicting id's
    this.noJsVariantSelector = this.querySelector(
      this.selectors.noJsVariantSelector,
    );
    this.noJsVariantSelector?.remove();

    this.variantInputs = this.querySelectorAll(this.selectors.variantInput);
    this.variantInputs.forEach((input) => {
      input.addEventListener("change", this.onVariantChange.bind(this), {
        signal: this.activeVariantsController.signal,
      });
    });

    // Listeners for tabbing and selecting swatches and chips specifically
    this.variantButton = this.querySelectorAll(this.selectors.variantButton);
    this.variantButton.forEach((button) => {
      button.addEventListener(
        "keydown",
        (event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();

            const swatchButton = button.getAttribute("js-swatch");
            const chipButton = button.getAttribute("js-chip");

            if (swatchButton !== null) {
              const swatchWrapper = event.target.parentElement;
              const inputHtml = swatchWrapper.previousElementSibling;

              this.onVariantChangeA11y(inputHtml);
            } else if (chipButton !== null) {
              this.onVariantChangeA11y(event.target.previousElementSibling);
            }
          }
        },
        {
          signal: this.activeVariantsController.signal,
        },
      );
    });
  }

  onVariantChange(event) {
    const input = event.target;
    this.variantLayout = input.getAttribute("data-variant-layout");
    this.swatchOption = input.getAttribute("data-swatch-option");
    this.logEcopack("onVariantChange:start", {
      changedValue: this.getControlOptionValue(input),
      changedTarget: this.formatDebugTarget(input),
      variantLayout: this.variantLayout,
    });

    switch (this.variantLayout) {
      case "chips":
        input.parentElement.parentElement
          .querySelector(".chip--selected")
          .classList.remove("chip--selected");

        input.parentElement
          .querySelector("[js-chip]")
          .classList.add("chip--selected");
        break

      case "swatches":
        input.parentElement.parentElement
          .querySelector(".swatch--selected")
          .classList.remove("swatch--selected");

        input.parentElement
          .querySelector("[js-swatch]")
          .classList.add("swatch--selected");
        break
    }

    let optionValueElement;
    if (this.variantLayout === "swatches" || this.variantLayout === "chips") {
      optionValueElement = input;
    } else if (this.variantLayout === "dropdown") {
      optionValueElement = input.selectedOptions[0];
    }

    this.syncEcoPrice(optionValueElement);

    // The current main product can either be the PDP's/Featured Prod or the product within the Quick view
    // TODO: Dry up repetition after binding `this` to onVariantChange to get scope and altering it's internal use of `this`
    const currentMainProductSection = input.closest("[js-product-section]");
    const productWithinQuickView =
      currentMainProductSection.dataset.sectionType === "quick-view-product";

    const variantChangingEvent = new CustomEvent(
      EVENTS.PRODUCT_VARIANT_CHANGING,
      {
        bubbles: true,
        detail: {
          newOptionInput: optionValueElement,
          withinQuickView: productWithinQuickView,
        },
      },
    );

    currentMainProductSection.dispatchEvent(variantChangingEvent);
    this.logEcopack("onVariantChange:end", {
      changedValue: this.getControlOptionValue(optionValueElement),
      changedTarget: this.formatDebugTarget(optionValueElement),
    });
  }

  onVariantChangeA11y(inputHtml) {
    this.syncEcoPrice(inputHtml);

    // The current main product can either be the PDP's/Featured Prod or the product within the Quick view
    const currentMainProductSection = this.closest("[js-product-section]");
    const productWithinQuickView =
      currentMainProductSection.dataset.sectionType === "quick-view-product";

    const variantChangingEvent = new CustomEvent(
      EVENTS.PRODUCT_VARIANT_CHANGING,
      {
        bubbles: true,
        detail: {
          newOptionInput: inputHtml,
          withinQuickView: productWithinQuickView,
        },
      },
    );

    currentMainProductSection.dispatchEvent(variantChangingEvent);
  }

  handleRepaint() {
    this.logEcopack("handleRepaint:start");
    this.activeVariantsController.abort();
    this.initActiveVariants();
    this.initEcoPriceModels();
    this.syncEcoPriceToSelectedVariant();
    this.initEcopack();
    this.logEcopack("handleRepaint:end");
  }

  initEcopack() {
    this.ecopackController?.abort?.();
    this.ecopackController = new AbortController();

    this.ecopackToggle = this.getEcopackToggle();
    if (!this.ecopackToggle) {
      this.logEcopack("initEcopack:no-toggle");
      return;
    }

    if (this.getStoredEcopackActive()) {
      this.ecopackToggle.checked = true;
    }

    this.logEcopack("initEcopack:toggle-found", {
      toggleChecked: this.ecopackToggle.checked,
    });

    this.ecopackToggle.addEventListener(
      "change",
      this.handleEcopackToggleChange.bind(this),
      { signal: this.ecopackController.signal },
    );

    this.syncEcopackState();
  }

  getEcopackToggle() {
    return this.productSection?.querySelector(this.selectors.ecopackToggle);
  }

  isEcopackEnabled() {
    const hasRenderedEcopack = Boolean(
      this.ecopackToggle || this.getEcopackToggle(),
    );

    return hasRenderedEcopack || this.dataset.ecopackEnabled === "true";
  }

  getEcopackTargetValue() {
    return (this.dataset.ecopackTargetValue || "").trim();
  }

  normalizeOptionValue(value) {
    return (value || "")
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\bgramos?\b/g, "g")
      .replace(/\bgrs?\b/g, "g")
      .replace(/\s+/g, "")
      .replace(/[^a-z0-9]/g, "");
  }

  getNumericTokens(value) {
    return this.normalizeOptionValue(value).match(/\d+/g) || [];
  }

  matchesEcopackTarget(controlValue, targetValue) {
    const normalizedControlValue = this.normalizeOptionValue(controlValue);
    const normalizedTargetValue = this.normalizeOptionValue(targetValue);

    if (!normalizedControlValue || !normalizedTargetValue) return false;
    if (normalizedControlValue === normalizedTargetValue) return true;

    const controlNumbers = this.getNumericTokens(controlValue);
    const targetNumbers = this.getNumericTokens(targetValue);

    if (controlNumbers.length || targetNumbers.length) {
      if (controlNumbers.length !== targetNumbers.length) return false;

      const sameNumericTokens = controlNumbers.every(
        (token, index) => token === targetNumbers[index],
      );

      if (!sameNumericTokens) return false;
    }

    return (
      normalizedControlValue.includes(normalizedTargetValue) ||
      normalizedTargetValue.includes(normalizedControlValue)
    );
  }

  getStoredEcopackActive() {
    return this.productSection?.dataset?.ecopackActive === "true";
  }

  setStoredEcopackActive(active) {
    if (!this.productSection) return;

    if (active) {
      this.productSection.dataset.ecopackActive = "true";
    } else {
      delete this.productSection.dataset.ecopackActive;
    }
  }

  getVariantGroups() {
    return Array.from(this.querySelectorAll(this.selectors.variantGroup));
  }

  formatDebugTarget(target) {
    if (!(target instanceof Element)) return String(target || "null");

    const className =
      typeof target.className === "string" ? target.className.trim() : "";
    const classSuffix = className ? `.${className.replace(/\s+/g, ".")}` : "";
    const idSuffix = target.id ? `#${target.id}` : "";

    return `${target.tagName.toLowerCase()}${idSuffix}${classSuffix}`;
  }

  getEcopackDebugSnapshot(extra = {}) {
    const groups = this.getVariantGroups().map((group) => ({
      optionName: group.dataset.optionName || "",
      optionPosition: group.dataset.optionPosition || "",
      locked: group.dataset.ecopackLocked === "true",
      values: this.getGroupControls(group).map((control) => ({
        value: this.getControlOptionValue(control),
        normalizedValue: this.normalizeOptionValue(
          this.getControlOptionValue(control),
        ),
        selected: this.isControlSelected(control),
        disabled: "disabled" in control ? Boolean(control.disabled) : false,
        target: this.formatDebugTarget(control),
      })),
    }));

    return {
      ecopackEnabled: this.isEcopackEnabled(),
      ecopackTargetValue: this.getEcopackTargetValue(),
      ecopackTargetNormalized: this.normalizeOptionValue(
        this.getEcopackTargetValue(),
      ),
      storedActive: this.getStoredEcopackActive(),
      toggleChecked: this.ecopackToggle?.checked,
      toggleDisabled: this.ecopackToggle?.disabled,
      groupCount: groups.length,
      groups,
      ...extra,
    };
  }

  logEcopack(message, extra = {}) {
    if (!this._debugEcopack) return;
    console.log(
      `[ProductBlockVariantSelector][Ecopack] ${message}`,
      this.getEcopackDebugSnapshot(extra),
    );
  }

  getGroupControls(group) {
    if (!(group instanceof Element)) return [];

    const dropdown = group.querySelector(
      `${this.selectors.variantInput}[data-variant-layout="dropdown"]`,
    );
    if (dropdown instanceof HTMLSelectElement) {
      return Array.from(dropdown.options);
    }

    return Array.from(
      group.querySelectorAll(`${this.selectors.variantInput}[type="radio"]`),
    );
  }

  getControlOptionValue(control) {
    if (!(control instanceof Element)) return "";
    return (control.dataset.optionValue || "").trim();
  }

  getMatchingEcopackGroups(targetValue) {
    if (!targetValue) return [];

    const matchingGroups = this.getVariantGroups().filter((group) =>
      this.getGroupControls(group).some(
        (control) =>
          this.matchesEcopackTarget(
            this.getControlOptionValue(control),
            targetValue,
          ),
      ),
    );

      this.logEcopack("getMatchingEcopackGroups", {
        targetValue,
      normalizedTargetValue: this.normalizeOptionValue(targetValue),
      matchingGroups: matchingGroups.map((group) => ({
        optionName: group.dataset.optionName || "",
        optionPosition: group.dataset.optionPosition || "",
      })),
    });

    return matchingGroups;
  }

  resolveEcopackTarget() {
    if (!this.isEcopackEnabled()) return null;

    const targetValue = this.getEcopackTargetValue();
    if (!targetValue) return null;

    const matchingGroups = this.getMatchingEcopackGroups(targetValue);
    if (matchingGroups.length !== 1) return null;

    const group = matchingGroups[0];
    const control = this.getGroupControls(group).find(
      (entry) => this.matchesEcopackTarget(this.getControlOptionValue(entry), targetValue),
    );

    if (!control) {
      this.logEcopack("resolveEcopackTarget:no-control", {
        targetValue,
        normalizedTargetValue: this.normalizeOptionValue(targetValue),
      });
      return null;
    }

    this.logEcopack("resolveEcopackTarget:resolved", {
      groupOptionName: group.dataset.optionName || "",
      groupOptionPosition: group.dataset.optionPosition || "",
      controlValue: this.getControlOptionValue(control),
      controlTarget: this.formatDebugTarget(control),
    });

    return { group, control, targetValue };
  }

  isControlUnavailable(control) {
    if (!(control instanceof Element)) return true;

    if ("disabled" in control && control.disabled) {
      return control.dataset.ecopackLockDisabled !== "true";
    }

    return control.classList.contains("disabled");
  }

  isControlSelected(control) {
    if (control instanceof HTMLOptionElement) {
      return control.selected;
    }

    if (control instanceof HTMLInputElement) {
      return control.checked;
    }

    return false;
  }

  selectControl(control) {
    if (control instanceof HTMLOptionElement) {
      const select = control.parentElement;
      if (!(select instanceof HTMLSelectElement)) return false;
      if (select.value === control.value && control.selected) return false;

      select.value = control.value;
      select.dispatchEvent(new Event("change", { bubbles: true }));
      this.logEcopack("selectControl:option", {
        controlValue: this.getControlOptionValue(control),
        controlTarget: this.formatDebugTarget(control),
      });
      return true;
    }

    if (control instanceof HTMLInputElement) {
      if (control.checked) return false;

      control.checked = true;
      control.dispatchEvent(new Event("change", { bubbles: true }));
      this.logEcopack("selectControl:input", {
        controlValue: this.getControlOptionValue(control),
        controlTarget: this.formatDebugTarget(control),
      });
      return true;
    }

    return false;
  }

  setControlLockedState(control, locked) {
    if (!(control instanceof Element)) return;

    const label = control instanceof HTMLOptionElement ? null : control.closest("label");
    const variantButton = label?.querySelector?.(this.selectors.variantButton);

    if (locked) {
      if (
        "disabled" in control &&
        control.disabled === false
      ) {
        control.disabled = true;
        control.dataset.ecopackLockDisabled = "true";
      }

      if (label) {
        label.dataset.ecopackLocked = "true";
        label.setAttribute("aria-disabled", "true");
      }

      if (
        variantButton &&
        "disabled" in variantButton &&
        variantButton.disabled === false
      ) {
        variantButton.disabled = true;
        variantButton.dataset.ecopackLockDisabled = "true";
      }

      return;
    }

    if (control.dataset.ecopackLockDisabled === "true") {
      control.disabled = false;
      delete control.dataset.ecopackLockDisabled;
    }

    if (label) {
      delete label.dataset.ecopackLocked;
      label.removeAttribute("aria-disabled");
    }

    if (
      variantButton?.dataset?.ecopackLockDisabled === "true" &&
      "disabled" in variantButton
    ) {
      variantButton.disabled = false;
      delete variantButton.dataset.ecopackLockDisabled;
    }
  }

  setGroupLockedState(group, targetControl, locked) {
    if (!(group instanceof Element)) return;

    const targetValue = this.getControlOptionValue(targetControl);
    const controls = this.getGroupControls(group);

    if (locked) {
      group.dataset.ecopackLocked = "true";
    } else {
      delete group.dataset.ecopackLocked;
    }

    controls.forEach((control) => {
      const isTarget = this.getControlOptionValue(control) === targetValue;
      this.setControlLockedState(control, locked && !isTarget);
    });

    this.logEcopack("setGroupLockedState", {
      locked,
      groupOptionName: group.dataset.optionName || "",
      targetValue,
    });
  }

  clearEcopackLocks() {
    this.querySelectorAll("[data-ecopack-lock-disabled='true']").forEach(
      (element) => {
        if ("disabled" in element) {
          element.disabled = false;
        }
        delete element.dataset.ecopackLockDisabled;
      },
    );

    this.querySelectorAll("[data-ecopack-locked='true']").forEach((element) => {
      delete element.dataset.ecopackLocked;
      element.removeAttribute("aria-disabled");
    });
  }

  syncEcopackAvailability() {
    if (!this.ecopackToggle) {
      return { configured: false, available: false };
    }

    const resolvedTarget = this.resolveEcopackTarget();
    if (resolvedTarget == null) {
      this.ecopackToggle.disabled = false;
      this.clearEcopackLocks();
      this.logEcopack("syncEcopackAvailability:not-configured");
      return { configured: false, available: true };
    }

    const isAvailable = !this.isControlUnavailable(resolvedTarget.control);

    this.ecopackToggle.disabled = !isAvailable;

    if (!isAvailable && this.ecopackToggle.checked) {
      this.ecopackToggle.checked = false;
      this.setStoredEcopackActive(false);
      this.clearEcopackLocks();
    }

    this.logEcopack("syncEcopackAvailability:configured", {
      available: isAvailable,
      resolvedControlValue: this.getControlOptionValue(resolvedTarget.control),
      resolvedControlTarget: this.formatDebugTarget(resolvedTarget.control),
    });

    return { configured: true, available: isAvailable };
  }

  activateEcopack() {
    const resolvedTarget = this.resolveEcopackTarget();
    if (!resolvedTarget) {
      this.logEcopack("activateEcopack:no-target");
      return { success: false, didTriggerVariantChange: false };
    }

    const { group, control } = resolvedTarget;
    if (this.isControlUnavailable(control)) {
      this.logEcopack("activateEcopack:target-unavailable", {
        controlValue: this.getControlOptionValue(control),
        controlTarget: this.formatDebugTarget(control),
      });
      return { success: false, didTriggerVariantChange: false };
    }

    this.clearEcopackLocks();
    this.setGroupLockedState(group, control, true);

    const result = {
      success: true,
      didTriggerVariantChange: this.selectControl(control),
    };

    this.logEcopack("activateEcopack:done", result);

    return result;
  }

  deactivateEcopack() {
    this.clearEcopackLocks();
  }

  dispatchEcopackStateChanged() {
    if (!this.productSection) return;

    this.productSection.dispatchEvent(
      new CustomEvent(EVENTS.PRODUCT_ECOPACK_STATE_CHANGED, {
        bubbles: true,
        detail: {
          active: Boolean(this.ecopackToggle?.checked),
          targetValue: this.getEcopackTargetValue(),
        },
      }),
    );
  }

  handleEcopackToggleChange() {
    this.setStoredEcopackActive(Boolean(this.ecopackToggle?.checked));
    this.logEcopack("handleEcopackToggleChange:start");

    const { configured, available } = this.syncEcopackAvailability();
    if (!configured) {
      this.logEcopack("handleEcopackToggleChange:not-configured");
      this.dispatchEcopackStateChanged();
      return;
    }

    if (!available) {
      this.logEcopack("handleEcopackToggleChange:not-available");
      this.dispatchEcopackStateChanged();
      return;
    }

    if (this.ecopackToggle.checked) {
      const { success, didTriggerVariantChange } = this.activateEcopack();
      if (!success) {
        this.ecopackToggle.checked = false;
        this.setStoredEcopackActive(false);
        this.deactivateEcopack();
        this.dispatchEcopackStateChanged();
        return;
      }

      if (!didTriggerVariantChange) {
        this.logEcopack("handleEcopackToggleChange:active-no-variant-change");
        this.dispatchEcopackStateChanged();
      }

      return;
    }

    this.deactivateEcopack();
    this.setStoredEcopackActive(false);
    this.logEcopack("handleEcopackToggleChange:deactivated");
    this.dispatchEcopackStateChanged();
  }

  syncEcopackState() {
    const { configured, available } = this.syncEcopackAvailability();
    if (!this.ecopackToggle || !configured || !available) return;

    if (this.ecopackToggle.checked) {
      this.activateEcopack();
    } else {
      this.deactivateEcopack();
    }
  }

  getEcoPriceElements() {
    const currentMainProductSection = this.closest("[js-product-section]");
    return Array.from(
      currentMainProductSection?.querySelectorAll(this.selectors.ecoPrice) || [],
    );
  }

  getVariantPriceCents(optionValueElement) {
    const cents = Number.parseInt(
      optionValueElement?.dataset?.variantPriceCents || "",
      10,
    );

    return Number.isFinite(cents) ? cents : null;
  }

  getVariantCompareAtPriceCents(optionValueElement) {
    const cents = Number.parseInt(
      optionValueElement?.dataset?.variantCompareAtPriceCents || "",
      10,
    );

    return Number.isFinite(cents) ? cents : null;
  }

  inferEcoDiscountBasisPoints(originalPriceCents, discountedPriceCents) {
    if (
      !Number.isFinite(originalPriceCents) ||
      !Number.isFinite(discountedPriceCents) ||
      originalPriceCents <= discountedPriceCents
    ) {
      return null;
    }

    const rawBasisPoints =
      ((originalPriceCents - discountedPriceCents) * 10000) /
      originalPriceCents;
    const candidateSteps = [100, 50, 25, 10, 5, 1];

    for (const step of candidateSteps) {
      const roundedBasisPoints = Math.round(rawBasisPoints / step) * step;

      for (let offset = -2; offset <= 2; offset += 1) {
        const candidate = roundedBasisPoints + offset * step;
        if (candidate <= 0 || candidate >= 10000) continue;

        const recalculatedDiscountedPrice =
          originalPriceCents -
          Math.floor((originalPriceCents * candidate) / 10000);

        if (recalculatedDiscountedPrice === discountedPriceCents) {
          return candidate;
        }
      }
    }

    return null;
  }

  getMoneyCentsFromNode(node) {
    if (!(node instanceof Element)) return null;

    const dataCents = Number.parseInt(node.getAttribute("data-cents") || "", 10);
    if (Number.isFinite(dataCents)) {
      return dataCents;
    }

    const textContent = node.textContent || "";
    if (!/\d/.test(textContent)) return null;

    return this.parseMoneyStringToCents(textContent);
  }

  setMoneyNodeValue(node, cents) {
    if (!(node instanceof Element) || !Number.isFinite(cents)) return;

    node.setAttribute("data-cents", `${cents}`);
    node.textContent = this.formatMoney(cents);
  }

  initEcoPriceModels() {
    const ecoPriceSourceOptionValueElement =
      this.getEcoPriceSourceOptionValueElement();
    const variantPriceCents = this.getVariantPriceCents(
      ecoPriceSourceOptionValueElement,
    );

    if (!Number.isFinite(variantPriceCents) || variantPriceCents <= 0) return;

    this.getEcoPriceElements().forEach((element) => {
      if (element.dataset.ecoPriceModelInitialized === "true") return;

      const originalPriceElement = element.querySelector(
        this.selectors.ecoOriginalPrice,
      );
      const discountedPriceElement = element.querySelector(
        this.selectors.ecoDiscountedPrice,
      );
      const originalPriceCents = this.getMoneyCentsFromNode(originalPriceElement);
      const discountedPriceCents = this.getMoneyCentsFromNode(
        discountedPriceElement,
      );

      if (Number.isFinite(originalPriceCents)) {
        element.dataset.ecoOriginalRatio = (
          originalPriceCents / variantPriceCents
        ).toString();
      }

      if (Number.isFinite(discountedPriceCents)) {
        element.dataset.ecoDiscountedRatio = (
          discountedPriceCents / variantPriceCents
        ).toString();
      }

      const ecoDiscountBasisPoints = this.inferEcoDiscountBasisPoints(
        originalPriceCents,
        discountedPriceCents,
      );

      if (Number.isFinite(ecoDiscountBasisPoints)) {
        element.dataset.ecoDiscountBasisPoints = `${ecoDiscountBasisPoints}`;
      }

      if (
        Number.isFinite(originalPriceCents) ||
        Number.isFinite(discountedPriceCents)
      ) {
        element.dataset.ecoPriceModelInitialized = "true";
      }
    });
  }

  parseMoneyStringToCents(value) {
    if (typeof value !== "string") return 0;

    const sanitized = value.replace(/[^\d.,-]/g, "");
    if (!sanitized) return 0;

    const lastComma = sanitized.lastIndexOf(",");
    const lastPeriod = sanitized.lastIndexOf(".");
    const decimalIndex = Math.max(lastComma, lastPeriod);

    let normalized = sanitized;
    if (decimalIndex >= 0) {
      const integerPart = sanitized.slice(0, decimalIndex).replace(/[.,]/g, "");
      const decimalPart = sanitized.slice(decimalIndex + 1).replace(/[.,]/g, "");
      normalized = `${integerPart}.${decimalPart}`;
    } else {
      normalized = sanitized.replace(/[.,]/g, "");
    }

    const parsedValue = Number.parseFloat(normalized);
    if (Number.isNaN(parsedValue)) return 0;

    return Math.round(parsedValue * 100);
  }

  formatWithDelimiters(
    cents,
    precision = 2,
    thousands = ",",
    decimal = ".",
  ) {
    if (Number.isNaN(cents) || cents == null) return "0";

    const number = (cents / 100).toFixed(precision);
    const parts = number.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);

    if (precision === 0) {
      return parts[0];
    }

    return parts.join(decimal);
  }

  formatMoney(cents) {
    const format = window.theme?.moneyFormat || "{{amount}}";
    const placeholderMatch = format.match(/\{\{\s*(\w+)\s*\}\}/);
    const placeholder = placeholderMatch?.[1] || "amount";

    let formattedValue;
    switch (placeholder) {
      case "amount":
        formattedValue = this.formatWithDelimiters(cents, 2, ",", ".");
        break;
      case "amount_no_decimals":
        formattedValue = this.formatWithDelimiters(cents, 0, ",", ".");
        break;
      case "amount_with_comma_separator":
        formattedValue = this.formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        formattedValue = this.formatWithDelimiters(cents, 0, ".", ",");
        break;
      case "amount_with_apostrophe_separator":
        formattedValue = this.formatWithDelimiters(cents, 2, "'", ".");
        break;
      default:
        formattedValue = this.formatWithDelimiters(cents, 2, ",", ".");
        break;
    }

    return format.replace(/\{\{\s*\w+\s*\}\}/, formattedValue);
  }

  getCurrentOptionValueElement() {
    const checkedInput = this.querySelector(
      `${this.selectors.variantInput}[type="radio"]:checked`,
    );
    if (checkedInput) return checkedInput;

    const dropdown = this.querySelector(
      `${this.selectors.variantInput}[data-variant-layout="dropdown"]`,
    );

    return dropdown?.selectedOptions?.[0] || null;
  }

  getEcoPriceSourceOptionValueElement(optionValueElement = null) {
    const resolvedTarget = this.resolveEcopackTarget();
    if (resolvedTarget?.control) {
      return resolvedTarget.control;
    }

    return optionValueElement || this.getCurrentOptionValueElement();
  }

  syncEcoPrice(optionValueElement) {
    const ecoPriceSourceOptionValueElement =
      this.getEcoPriceSourceOptionValueElement(optionValueElement);
    const priceDisplay =
      ecoPriceSourceOptionValueElement?.dataset?.variantPriceDisplay;
    const priceCents = this.getVariantPriceCents(
      ecoPriceSourceOptionValueElement,
    );
    const compareAtPriceCents = this.getVariantCompareAtPriceCents(
      ecoPriceSourceOptionValueElement,
    );
    if (!Number.isFinite(priceCents)) return;

    this.initEcoPriceModels();

    this.getEcoPriceElements().forEach((element) => {
      const originalPriceElement = element.querySelector(
        this.selectors.ecoOriginalPrice,
      );
      const discountedPriceElement = element.querySelector(
        this.selectors.ecoDiscountedPrice,
      );
      const discountedRatio = Number.parseFloat(
        element.dataset.ecoDiscountedRatio || "",
      );
      const ecoDiscountBasisPoints = Number.parseInt(
        element.dataset.ecoDiscountBasisPoints || "",
        10,
      );
      let updatedStructuredPrice = false;

      if (discountedPriceElement) {
        let discountedPriceCents = null;
        let originalPriceCents = priceCents;

        if (
          Number.isFinite(ecoDiscountBasisPoints) &&
          ecoDiscountBasisPoints > 0
        ) {
          originalPriceCents = priceCents;
          discountedPriceCents =
            priceCents -
            Math.floor((priceCents * ecoDiscountBasisPoints) / 10000);
        } else if (
          Number.isFinite(compareAtPriceCents) &&
          compareAtPriceCents > priceCents
        ) {
          originalPriceCents = compareAtPriceCents;
          discountedPriceCents = priceCents;
        } else if (Number.isFinite(discountedRatio) && discountedRatio < 1) {
          originalPriceCents = priceCents;
          discountedPriceCents = Math.round(priceCents * discountedRatio);
        } else {
          originalPriceCents = priceCents;
          discountedPriceCents = priceCents;
        }

        if (originalPriceElement) {
          this.setMoneyNodeValue(originalPriceElement, originalPriceCents);
        }
        this.setMoneyNodeValue(discountedPriceElement, discountedPriceCents);
        updatedStructuredPrice = true;
      } else if (originalPriceElement) {
        this.setMoneyNodeValue(originalPriceElement, priceCents);
        updatedStructuredPrice = true;
      }

      if (updatedStructuredPrice) {
        this.logEcopack("syncEcoPrice:ecopack-pricing", {
          priceSourceValue: this.getControlOptionValue(
            ecoPriceSourceOptionValueElement,
          ),
          priceSourceTarget: this.formatDebugTarget(
            ecoPriceSourceOptionValueElement,
          ),
          priceCents,
          compareAtPriceCents,
          ecoDiscountBasisPoints,
          discountedRatio,
        });
        return;
      }
      if (!priceDisplay || element.children.length > 0) return;

      element.textContent = priceDisplay;
    });
  }

  syncEcoPriceToSelectedVariant() {
    this.syncEcoPrice(this.getCurrentOptionValueElement());
  }

  disconnectedCallback() {
    this.controller.abort();
    this.ecopackController?.abort?.();
  }
}

if (!customElements.get("product-block-variant-selector")) {
  customElements.define(
    "product-block-variant-selector",
    ProductBlockVariantSelector,
  );
}

